import {EditButton} from '@github-ui/code-view-shared/components/EditButton'
import TableOfContentsPanel from '@github-ui/code-view-shared/components/TableOfContentsPanel'
import {DirectoryRichtextContent} from '@github-ui/code-view-shared/components/directory/DirectoryRichtextContent'
import {useEditPermissions} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {PreferredFileTypes, type RepoOverviewBlobPayload} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {updateUrl} from '@github-ui/history'
import {editBlobPath, repositoryTreePath} from '@github-ui/paths'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {BookIcon, CodeOfConductIcon, LawIcon, ListUnorderedIcon, PeopleIcon} from '@primer/octicons-react'
import {ActionMenu, Spinner, UnderlineNav} from '@primer/react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {Blankslate} from '@primer/react/experimental'

import styles from './OverviewRepoFiles.module.css'
import {useQuery} from '@github-ui/react-query'

export function OverviewFiles({
  initialFiles,
  shouldRecommendReadme,
  isPersonalRepo,
}: {
  initialFiles: RepoOverviewBlobPayload[] | undefined
  shouldRecommendReadme: boolean
  isPersonalRepo: boolean
}) {
  const repo = useCurrentRepository()
  const refInfo = useRefInfo()
  const {data: deferredOverviewFiles} = useQuery({
    queryKey: [repo.ownerLogin, repo.name, 'overview-files', refInfo.name],
    queryFn: async () => {
      const url = repositoryTreePath({
        repo: {
          ownerLogin: repo.ownerLogin,
          name: repo.name,
        },
        action: 'overview-files',
        commitish: refInfo.name,
      })
      const response = await verifiedFetchJSON(url)

      if (response.ok) {
        const data = await response.json()
        return data.files as RepoOverviewBlobPayload[]
      }

      return null
    },
    enabled: initialFiles?.some((file: RepoOverviewBlobPayload) => !file.loaded) ?? false,
  })

  const overviewFiles = deferredOverviewFiles || initialFiles

  const readme = overviewFiles?.find(file => file.preferredFileType === PreferredFileTypes.README)
  const coc = overviewFiles?.find(file => file.preferredFileType === PreferredFileTypes.CODE_OF_CONDUCT)
  const licenses = (overviewFiles ?? []).filter(file => file.preferredFileType === PreferredFileTypes.LICENSE)
  const securityPolicy = overviewFiles?.find(file => file.preferredFileType === PreferredFileTypes.SECURITY)
  const contributing = overviewFiles?.find(file => file.preferredFileType === PreferredFileTypes.CONTRIBUTING)

  let initialSelectedTab = 'readme-ov-file'
  if (!readme && !shouldRecommendReadme) {
    if (coc) {
      initialSelectedTab = 'coc-ov-file'
    } else if (contributing) {
      initialSelectedTab = 'contributing-ov-file'
    } else if (licenses.length > 0 && licenses[0]) {
      initialSelectedTab = `${licenses[0].tabName}-1-ov-file`
    } else if (securityPolicy) {
      initialSelectedTab = 'security-ov-file'
    }
  }

  const [selectedTab, setSelectedTab] = useState(initialSelectedTab)
  const containerRef = useRef<HTMLDivElement>(null)
  const {canEdit} = useEditPermissions()

  const getTabNames = useCallback(() => {
    const tabNames: string[] = []

    if (readme || shouldRecommendReadme) {
      tabNames.push('readme-ov-file')
    }

    if (coc) {
      tabNames.push('coc-ov-file')
    }

    if (contributing) {
      tabNames.push('contributing-ov-file')
    }

    if (licenses.length) {
      for (const [index, license] of licenses.entries()) {
        tabNames.push(`${license.tabName}-${index + 1}-ov-file`)
      }
    }

    if (securityPolicy) {
      tabNames.push('security-ov-file')
    }

    return tabNames
  }, [coc, licenses, contributing, readme, securityPolicy, shouldRecommendReadme])

  const readmeRef = useRef<HTMLAnchorElement>(null)
  const contributingRef = useRef<HTMLAnchorElement>(null)
  const securityRef = useRef<HTMLAnchorElement>(null)
  const cocRef = useRef<HTMLAnchorElement>(null)

  const onHashChange = useCallback(() => {
    const hash = window.location.hash.replace('#', '')

    const tabNames = getTabNames()

    if (hash && tabNames.includes(hash)) {
      // Move the hash to the query param and set the hash to the readme section id
      const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?tab=${hash}#readme`
      updateUrl(newUrl)
      setSelectedTab(hash)
    } else {
      const params = new URLSearchParams(window.location.search)
      const tabName = params.get('tab')

      if (tabName && tabNames.includes(tabName)) {
        setSelectedTab(tabName)
      }
    }

    window.requestAnimationFrame(() => {
      if (containerRef.current && window.location.hash === '#readme') {
        containerRef.current.scrollIntoView()
        if (hash === 'readme-ov-file' && readmeRef.current) {
          readmeRef.current.focus()
        } else if (hash === 'coc-ov-file' && cocRef.current) {
          cocRef.current.focus()
        } else if (hash === 'contributing-ov-file' && contributingRef.current) {
          contributingRef.current.focus()
        } else if (hash === 'security-ov-file' && securityRef.current) {
          securityRef.current.focus()
        }
      }
    })
  }, [getTabNames])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    onHashChange()

    window.addEventListener('hashchange', onHashChange)

    return () => {
      window.removeEventListener('hashchange', onHashChange)
    }
  }, [onHashChange])

  const onSelect = useCallback(
    (ev: React.MouseEvent | React.KeyboardEvent, tabId: string) => {
      ev.preventDefault()
      if (selectedTab === tabId) return
      setSelectedTab(tabId)

      const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?tab=${tabId}`
      updateUrl(newUrl)
    },
    [selectedTab],
  )

  if (!readme && !coc && !licenses.length && !securityPolicy && !shouldRecommendReadme && !contributing) {
    return null
  }

  let content: React.ReactElement | undefined = undefined

  let blob: RepoOverviewBlobPayload | undefined = undefined

  const selectedLicense = licenses.find((license, index) => `${license.tabName}-${index + 1}-ov-file` === selectedTab)

  if (selectedTab === 'readme-ov-file') {
    if (readme) {
      blob = readme
    } else if (shouldRecommendReadme) {
      content = (
        <Blankslate>
          <Blankslate.Visual>
            <BookIcon size="medium" />
          </Blankslate.Visual>
          <Blankslate.Heading>Add a README</Blankslate.Heading>
          <Blankslate.Description>
            {isPersonalRepo
              ? 'Add a README with an overview of your project.'
              : 'Help people interested in this repository understand your project.'}
          </Blankslate.Description>
          <Blankslate.PrimaryAction
            href={`${repositoryTreePath({
              repo,
              path: undefined,
              commitish: refInfo.name,
              action: 'new',
            })}?filename=README.md`}
          >
            Add a README
          </Blankslate.PrimaryAction>
        </Blankslate>
      )
    }
  } else if (selectedTab === 'coc-ov-file' && coc) {
    blob = coc
  } else if (selectedTab === 'contributing-ov-file' && contributing) {
    blob = contributing
  } else if (selectedLicense) {
    blob = selectedLicense
  } else if (securityPolicy) {
    blob = securityPolicy
  }

  if (blob && blob.path) {
    if (!blob.loaded) {
      content = (
        <div className={styles.Box}>
          <Spinner />
        </div>
      )
    } else {
      content = (
        <DirectoryRichtextContent
          errorMessage={blob.errorMessage}
          onAnchorClick={() => {
            const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?tab=${selectedTab}${window.location.hash}`
            updateUrl(newUrl)
          }}
          path={blob.path}
          richText={blob.richText}
          stickyHeaderHeight={50}
          timedOut={blob.timedOut}
        />
      )
    }
  }

  return (
    <div className={styles.Box_1}>
      <div ref={containerRef} className={styles.Box_2}>
        <div itemScope itemType="https://schema.org/abstract" className={styles.Box_3}>
          <UnderlineNav aria-label="Repository files" className={styles.UnderlineNav}>
            {(readme || shouldRecommendReadme) && (
              <UnderlineNav.Item
                icon={BookIcon}
                ref={readmeRef}
                aria-current={selectedTab === 'readme-ov-file' ? 'page' : undefined}
                onSelect={ev => onSelect(ev, 'readme-ov-file')}
              >
                {readme?.tabName || 'README'}
              </UnderlineNav.Item>
            )}
            {coc && (
              <UnderlineNav.Item
                icon={CodeOfConductIcon}
                ref={cocRef}
                aria-current={selectedTab === 'coc-ov-file' ? 'page' : undefined}
                onSelect={ev => onSelect(ev, 'coc-ov-file')}
              >
                {coc?.tabName || 'Code of conduct'}
              </UnderlineNav.Item>
            )}
            {contributing && (
              <UnderlineNav.Item
                icon={PeopleIcon}
                ref={contributingRef}
                aria-current={selectedTab === 'contributing-ov-file' ? 'page' : undefined}
                onSelect={ev => onSelect(ev, 'contributing-ov-file')}
              >
                {contributing?.tabName || 'Contributing'}
              </UnderlineNav.Item>
            )}
            {licenses.length
              ? licenses.map((license, index) => {
                  const tabId = `${license.tabName}-${index + 1}-ov-file`

                  return (
                    <UnderlineNav.Item
                      key={license.path}
                      icon={LawIcon}
                      aria-current={selectedTab === tabId ? 'page' : undefined}
                      onSelect={ev => onSelect(ev, tabId)}
                    >
                      {license.tabName?.toLowerCase() !== 'license' ? `${license.tabName} license` : 'License'}
                    </UnderlineNav.Item>
                  )
                })
              : null}
            {securityPolicy && (
              <UnderlineNav.Item
                icon={LawIcon}
                ref={securityRef}
                aria-current={selectedTab === 'security-ov-file' ? 'page' : undefined}
                onSelect={ev => onSelect(ev, 'security-ov-file')}
              >
                {securityPolicy?.tabName || 'Security policy'}
              </UnderlineNav.Item>
            )}
          </UnderlineNav>
          {canEdit && blob && (
            <EditButton
              editPath={editBlobPath({
                owner: repo.ownerLogin,
                repo: blob.repoName,
                commitish: blob.refName,
                filePath: blob.path,
              })}
              editTooltip="Edit file"
              // eslint-disable-next-line @github-ui/github-monorepo/no-sx
              customSx={{mr: 2, height: '28px'}}
            />
          )}
          {blob?.headerInfo?.toc && blob?.headerInfo?.toc?.length >= 2 && (
            <ActionMenu>
              <ActionMenu.Button
                icon={ListUnorderedIcon}
                variant="invisible"
                aria-label="Outline"
                className={styles.ActionMenu_Button}
              >
                Outline
              </ActionMenu.Button>

              <ActionMenu.Overlay align="end" className={styles.ActionMenu_Overlay}>
                <TableOfContentsPanel toc={blob.headerInfo.toc} />
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
        </div>
        {content}
      </div>
    </div>
  )
}
